import React, { useState } from 'react';
import { Box, Button, TextField, Typography, Grid, Paper, CircularProgress, FormControl, InputLabel, Select, MenuItem, Alert, Card, CardContent, Divider } from '@mui/material';
import { useImage } from '../../hooks/useImage';

/**
 * Image Generator Component
 * A reusable component for generating images using AI
 * 
 * @param {Object} props - Component props
 * @param {string} props.projectId - Optional project ID for saving images
 * @param {Function} props.onImageGenerated - Optional callback when image is generated
 * @param {Function} props.onImageSaved - Optional callback when image is saved to project
 * @param {boolean} props.showSaveOption - Whether to show the save option (default: true)
 * @param {Object} props.defaultOptions - Default options for image generation
 * @param {boolean} props.showFullControls - Whether to show all controls (default: true)
 * @param {string} props.initialPrompt - Initial value for the prompt field
 * @param {boolean} props.compactLayout - Whether to use a compact layout (default: false)
 */
const ImageGenerator = ({ 
  projectId,
  onImageGenerated,
  onImageSaved,
  showSaveOption = true,
  defaultOptions = {},
  showFullControls = true,
  initialPrompt = '',
  compactLayout = false
}) => {
  const { 
    loading, 
    generatedImages, 
    generateImage, 
    saveImageToProject, 
    clearGeneratedImages 
  } = useImage();
  
  const [prompt, setPrompt] = useState(initialPrompt);
  const [error, setError] = useState(null);
  const [options, setOptions] = useState({
    model: 'dall-e-3',
    size: '1024x1024',
    style: 'natural',
    quality: 'standard',
    n: 1,
    ...defaultOptions
  });

  const handlePromptChange = (e) => {
    setPrompt(e.target.value);
  };

  const handleOptionChange = (key, value) => {
    setOptions(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleGenerateImage = async () => {
    if (!prompt.trim()) return;
    
    // Clear any previous errors
    setError(null);
    
    try {
      // Prepare options based on model
      let adjustedOptions;
      
      // Handle model-specific parameters
      if (options.model === 'gpt-image-1') {
        // GPT Image 1 only supports minimal parameters
        // Keep only model, prompt, size, and n
        adjustedOptions = {
          model: options.model,
          size: options.size,
        };
        
        // Add n if it exists
        if (options.n !== undefined) {
          adjustedOptions.n = options.n;
        }
      } else {
        // DALL-E models use response_format
        adjustedOptions = { 
          ...options,
          response_format: options.response_format || 'b64_json'
        };
      }
      
      const result = await generateImage(prompt, adjustedOptions);
      
      // API response structure may vary depending on model
      // The result should contain data with URLs
      if (onImageGenerated) {
        // Pass the whole result to the callback
        onImageGenerated(result);
      }
    } catch (error) {
      console.error('Failed to generate image:', error);
      
      // Handle common errors
      if (error.response) {
        // Server responded with an error status
        const status = error.response.status;
        const errorData = error.response.data || {};
        
        if (status === 401) {
          setError("API key authentication failed. Please check your OpenAI API key.");
        } else if (status === 400) {
          setError(errorData.message || "Bad request. Please check your prompt and try again.");
        } else if (status === 429) {
          setError("Rate limit exceeded. Please try again later.");
        } else {
          setError(errorData.message || "An error occurred while generating the image.");
        }
      } else if (error.message === 'OpenAI API key is not configured') {
        setError("OpenAI API key is not configured. Please add your API key in the server settings.");
      } else if (error.message) {
        setError(error.message);
      } else {
        setError("An unexpected error occurred. Please try again later.");
      }
    }
  };

  const handleSaveImage = async (imageUrl) => {
    if (!projectId) return;
    
    // Clear any previous errors
    setError(null);
    
    try {
      const savedImage = await saveImageToProject(projectId, imageUrl, prompt, options);
      if (onImageSaved) {
        onImageSaved(savedImage);
      }
    } catch (error) {
      console.error('Failed to save image:', error);
      
      // Handle common errors
      if (error.response) {
        // Server responded with an error status
        const errorData = error.response.data || {};
        setError(errorData.message || "Failed to save image.");
      } else if (error.message) {
        setError(error.message);
      } else {
        setError("An unexpected error occurred while saving the image.");
      }
    }
  };

  const handleClearImages = () => {
    clearGeneratedImages();
  };

  return (
    <Box sx={{ width: '100%' }}>
      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert 
            severity="error" 
            onClose={() => setError(null)}
            sx={{ '& .MuiAlert-message': { width: '100%' } }}
          >
            {error}
          </Alert>
        </Box>
      )}
      
      {compactLayout ? (
        // Compact layout for use in project creation form
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="medium">Generate Images</Typography>
              {generatedImages.length > 0 && (
                <Button
                  variant="text"
                  color="secondary"
                  onClick={handleClearImages}
                  disabled={loading}
                  size="small"
                >
                  remove it
                </Button>
              )}
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Create AI-generated images by describing what you want to see. Images can be saved after generation.
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                Image description
              </Typography>
              <TextField
                fullWidth
                placeholder="Describe the image you want to generate..."
                value={prompt}
                onChange={handlePromptChange}
                multiline
                rows={2}
                variant="outlined"
                disabled={loading}
                size="small"
              />
            </Box>
            
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                  Model
                </Typography>
                <FormControl fullWidth variant="outlined" size="small">
                  <Select
                    value={options.model}
                    onChange={(e) => handleOptionChange('model', e.target.value)}
                    disabled={loading}
                    size="small"
                  >
                    <MenuItem value="dall-e-3">DALL-E 3</MenuItem>
                    <MenuItem value="gpt-image-1">GPT Image 1</MenuItem>
                    <MenuItem value="dall-e-2">DALL-E 2</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                  Size
                </Typography>
                <FormControl fullWidth variant="outlined" size="small">
                  <Select
                    value={options.size}
                    onChange={(e) => handleOptionChange('size', e.target.value)}
                    disabled={loading}
                    size="small"
                  >
                    <MenuItem value="1024x1024">1024x1024 (Square)</MenuItem>
                    <MenuItem value="1024x1792">1024x1792 (Portrait)</MenuItem>
                    <MenuItem value="1792x1024">1792x1024 (Landscape)</MenuItem>
                    {options.model === 'dall-e-2' && <MenuItem value="512x512">512x512</MenuItem>}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                  Style
                </Typography>
                <FormControl fullWidth variant="outlined" size="small">
                  <Select
                    value={options.style}
                    onChange={(e) => handleOptionChange('style', e.target.value)}
                    disabled={loading || options.model === 'dall-e-2'}
                    size="small"
                  >
                    <MenuItem value="natural">Natural</MenuItem>
                    <MenuItem value="vivid">Vivid</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                  Quantity
                </Typography>
                <FormControl fullWidth variant="outlined" size="small">
                  <Select
                    value={options.n}
                    onChange={(e) => handleOptionChange('n', e.target.value)}
                    disabled={loading}
                    size="small"
                  >
                    {[1, 2, 3, 4].map(num => (
                      <MenuItem key={num} value={num}>{num}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerateImage}
              disabled={loading || !prompt.trim()}
              startIcon={loading && <CircularProgress size={20} color="inherit" />}
              fullWidth
              size="medium"
            >
              {loading ? 'Generating...' : 'Generate Image'}
            </Button>
          </CardContent>
          
          {generatedImages.length > 0 && (
            <>
              <Divider />
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  {generatedImages.map((img, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Paper elevation={1} sx={{ p: 1, position: 'relative', borderRadius: 1 }}>
                        {img.processing ? (
                          // Placeholder for processing image
                          <Box 
                            sx={{ 
                              width: '100%', 
                              height: '200px', 
                              display: 'flex', 
                              flexDirection: 'column',
                              alignItems: 'center', 
                              justifyContent: 'center',
                              backgroundColor: 'rgba(0,0,0,0.04)',
                              borderRadius: '4px'
                            }}
                          >
                            <CircularProgress 
                              size={40} 
                              thickness={4} 
                              sx={{ mb: 2 }}
                              variant={img.progress && img.progress > 0 ? "determinate" : "indeterminate"} 
                              value={img.progress || 0}
                            />
                            <Typography variant="body2" color="text.secondary" align="center" sx={{ px: 2 }}>
                              {img.message || "Generating image..."}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                              {img.progress && img.progress > 0 
                                ? `${Math.round(img.progress)}% complete`
                                : "This may take up to a minute"}
                            </Typography>
                            {img.requestId && (
                              <Typography variant="caption" color="text.secondary" sx={{ 
                                mt: 1, 
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                                color: 'primary.main'
                              }} className="streaming-indicator">
                                <span className="streaming-dot"></span>
                                Streaming image in progress...
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          // Regular image display
                          <img 
                            src={img.url} 
                            alt={`Generated image ${index + 1}`}
                            style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '4px' }}
                          />
                        )}
                        
                        {showSaveOption && projectId && img.url && !img.processing && (
                          <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleSaveImage(img.url)}
                              disabled={loading}
                              sx={{ textTransform: 'none' }}
                            >
                              Save
                            </Button>
                          </Box>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </>
          )}
        </Card>
      ) : (
        // Original layout for the full-sized dialog
        <>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Image Description"
              placeholder="Describe the image you want to generate..."
              value={prompt}
              onChange={handlePromptChange}
              multiline
              rows={3}
              variant="outlined"
              disabled={loading}
            />
          </Box>
          
          {showFullControls && (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Model</InputLabel>
                  <Select
                    value={options.model}
                    onChange={(e) => handleOptionChange('model', e.target.value)}
                    label="Model"
                    disabled={loading}
                  >
                    <MenuItem value="dall-e-3">DALL-E 3 (Recommended)</MenuItem>
                    <MenuItem value="gpt-image-1">GPT Image 1</MenuItem>
                    <MenuItem value="dall-e-2">DALL-E 2</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Size</InputLabel>
                  <Select
                    value={options.size}
                    onChange={(e) => handleOptionChange('size', e.target.value)}
                    label="Size"
                    disabled={loading}
                  >
                    <MenuItem value="1024x1024">1024x1024 (Square)</MenuItem>
                    <MenuItem value="1024x1792">1024x1792 (Portrait)</MenuItem>
                    <MenuItem value="1792x1024">1792x1024 (Landscape)</MenuItem>
                    {options.model === 'dall-e-2' && <MenuItem value="512x512">512x512 (DALL-E 2 only)</MenuItem>}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Style</InputLabel>
                  <Select
                    value={options.style}
                    onChange={(e) => handleOptionChange('style', e.target.value)}
                    label="Style"
                    disabled={loading || options.model === 'dall-e-2'}
                  >
                    <MenuItem value="natural">Natural</MenuItem>
                    <MenuItem value="vivid">Vivid</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Quality</InputLabel>
                  <Select
                    value={options.quality}
                    onChange={(e) => handleOptionChange('quality', e.target.value)}
                    label="Quality"
                    disabled={loading || options.model === 'dall-e-2'}
                  >
                    <MenuItem value="standard">Standard</MenuItem>
                    <MenuItem value="hd">HD</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel>Quantity</InputLabel>
                  <Select
                    value={options.n}
                    onChange={(e) => handleOptionChange('n', e.target.value)}
                    label="Quantity"
                    disabled={loading}
                  >
                    {[1, 2, 3, 4].map(num => (
                      <MenuItem key={num} value={num}>{num}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerateImage}
              disabled={loading || !prompt.trim()}
              startIcon={loading && <CircularProgress size={20} color="inherit" />}
            >
              {loading ? 'Generating...' : 'Generate Image'}
            </Button>
            
            {generatedImages.length > 0 && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleClearImages}
                disabled={loading}
              >
                Clear Results
              </Button>
            )}
          </Box>
          
          {generatedImages.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Generated Images
              </Typography>
              
              <Grid container spacing={2}>
                {generatedImages.map((img, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Paper elevation={2} sx={{ p: 1, position: 'relative' }}>
                      {img.processing ? (
                        // Placeholder for processing image
                        <Box 
                          sx={{ 
                            width: '100%', 
                            height: '200px', 
                            display: 'flex', 
                            flexDirection: 'column',
                            alignItems: 'center', 
                            justifyContent: 'center',
                            backgroundColor: 'rgba(0,0,0,0.04)',
                            borderRadius: '4px'
                          }}
                        >
                          <CircularProgress 
                            size={40} 
                            thickness={4} 
                            sx={{ mb: 2 }}
                            variant={img.progress && img.progress > 0 ? "determinate" : "indeterminate"} 
                            value={img.progress || 0}
                          />
                          <Typography variant="body2" color="text.secondary" align="center" sx={{ px: 2 }}>
                            {img.message || "Generating image..."}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                            {img.progress && img.progress > 0 
                              ? `${Math.round(img.progress)}% complete`
                              : "This may take up to a minute"}
                          </Typography>
                          {img.requestId && (
                            <Typography variant="caption" color="text.secondary" sx={{ 
                              mt: 1, 
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px',
                              color: 'primary.main'
                            }} className="streaming-indicator">
                              <span className="streaming-dot"></span>
                              Streaming image in progress...
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        // Regular image display
                        <img 
                          src={img.url} 
                          alt={`Generated image ${index + 1}`}
                          style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '4px' }}
                        />
                      )}
                      
                      {showSaveOption && projectId && img.url && !img.processing && (
                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleSaveImage(img.url)}
                            disabled={loading}
                            sx={{ textTransform: 'none' }}
                          >
                            Save
                          </Button>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ImageGenerator;